import React, { useState, useEffect, Fragment, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Gantt, ViewMode } from 'gantt-task-react';
import "gantt-task-react/dist/index.css";
import { TaskListTable } from "./GanttChart/TaskListTable";
import { TaskListHeader } from "./GanttChart/TaskListHeader";
import {
    openTaskEditDrawer,
    setEditableTask,
    fetchGanttTasksByProject,
    editTask,
    changeGanttViewMode,
    setGanttListVisibility,
    updateIsLoading
} from "../../../Settings/store/taskSlice";
import { fetchProjectPriorities, fetchProjectStatuses } from "../../../Settings/store/projectSlice";
import { useParams } from 'react-router-dom';
import { Box, Button, Card, Text, ThemeIcon } from '@mantine/core';
import useTwColorByName from "../../../ui/useTwColorByName";
import CustomTooltip from './GanttChart/CustomTaskTooltip';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconNotesOff } from '@tabler/icons-react';
import { translate } from '../../../../utils/i18n';


function prepareTasks(tasks, bgColor) {
    return tasks.map((task, i) => {
        // Map status or priority to colors if possible, otherwise fallback to assignee or default
        let barColor = undefined;
        let progressColor = undefined;

        // Status-based coloring (User Defined Status)
        if (task.internal_status && task.internal_status.color_code) {
            barColor = task.internal_status.color_code;
            progressColor = task.internal_status.color_code;
        } else if (task.statusName && task.status_color_code) {
            barColor = task.status_color_code;
            progressColor = task.status_color_code;
        }
        // Fallback to internal status if needed
        else if (task.status === 'COMPLETED') {
            barColor = '#10B981'; // Green
            progressColor = '#10B981';
        }

        // Priority coloring (optional fallback if status doesn't determine color)
        // If status color isn't set, try priority
        if (!barColor && task.priority && task.priority.color_code) {
            barColor = task.priority.color_code;
            progressColor = task.priority.color_code;
        }

        // Fallback to Assignee-based
        if (!barColor && task.assigned_to && task.assigned_to.name) {
            barColor = bgColor(task.assigned_to.name)["bg-color"];
            progressColor = bgColor(task.assigned_to.name)["bg-color"];
        }

        // Default
        if (!barColor) {
            barColor = '#ED7D31';
            progressColor = '#ED7D31';
        }


        const isMissing = !task.start || !task.end;
        const start = new Date(task.start);
        const end = new Date(task.end);

        const durationDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        let maxLength = 50;

        const displayName = task.name && task.name.length > maxLength
            ? `${task.name.slice(0, maxLength)}...`
            : task.name;

        return {
            ...task,
            name: displayName || 'Untitled Task',
            fullName: task.name || 'Untitled Task',
            type: 'task', // Explicitly set to 'task' to ensure it is draggable. 'project' type is often read-only.
            isDisabled: false, // Explicitly enable interaction
            start: start,
            end: end,
            // Use statusName for display, keep status for internal logic if needed
            displayStatus: (task.internal_status && task.internal_status.name) ? task.internal_status.name : (task.statusName || task.status),
            displayStatusColor: (task.internal_status && task.internal_status.color_code) ? task.internal_status.color_code : (task.status_color_code || (task.status === 'COMPLETED' ? '#10B981' : '#3b82f6')),
            progress: task.status === 'COMPLETED' ? 100 : 0,
            dependencies: task.dependencies && Array.isArray(task.dependencies) && task.dependencies.length > 0
                ? task.dependencies.map(depId => String(depId))
                : [],
            project: task.parent_id ? task.parent_id : undefined,
            styles: task.isMissingDates ? {
                backgroundColor: "rgba(237, 125, 49, 0.2)",
                backgroundSelectedColor: "rgba(57, 117, 141, 0.2)",
                progressColor: "transparent",
                progressSelectedColor: "transparent",
            } : {
                backgroundColor: barColor,
                progressColor: progressColor,
                backgroundSelectedColor: '#39758D',
                progressSelectedColor: '#39758D',
                borderRadius: '4px',
            },
        };
    });
}

const TaskGanttChart = () => {
    const bgColor = useTwColorByName();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { ganttTasks, ganttViewMode, ganttListIsVisible } = useSelector((state) => state.settings.task);
    const { loggedUserId } = useSelector((state) => state.auth.user);
    const { loggedInUser } = useSelector((state) => state.auth.session);

    const [tasks, setTasks] = useState([]);
    const [viewDate, setViewDate] = useState(new Date());

    useEffect(() => {
        if (ganttTasks && ganttTasks.length > 0) {
            const formattedTasks = prepareTasks(ganttTasks, bgColor);
            setTasks(formattedTasks);
        } else {
            setTasks([]);
        }
    }, [ganttTasks, bgColor]);

    const handleExpanderClick = (task) => {
        setTasks(tasks.map(t => (t.id === task.id ? task : t)));
    };

    const onDblClick = (task) => {
        const taskObj = tasks.find(t => Number(t.id) === Number(task.id));
        if (taskObj) {
            dispatch(setEditableTask(taskObj));
            dispatch(openTaskEditDrawer());
        }
    };

    const dynamicHeight = window.innerHeight - 320; // Adjusted calculation

    let columnWidth = 65;
    if (ganttViewMode === ViewMode.Year) {
        columnWidth = 350;
    } else if (ganttViewMode === ViewMode.Month) {
        columnWidth = 300;
    } else if (ganttViewMode === ViewMode.Week) {
        columnWidth = 250;
    }

    const [limit] = useState(15);
    const [offset, setOffset] = useState(0); // Kept for future pagination implementation if needed
    const [hasMore, setHasMore] = useState(true); // Kept for compatibility
    const [loadingMore, setLoadingMore] = useState(false);


    const fetchTasks = async (reset = false) => {
        setLoadingMore(true);
        // data for dropdowns
        dispatch(fetchProjectStatuses(id));
        dispatch(fetchProjectPriorities(id));

        await dispatch(fetchGanttTasksByProject({ id: id })).then((response) => {
            if (response.payload.status === 200) {
                dispatch(updateIsLoading(false))
            }
        });
        setLoadingMore(false);
    };

    useEffect(() => {
        dispatch(setGanttListVisibility(true));
        fetchTasks(true);
    }, [id]);

    const handleDateChange = async (task) => {
        const updatedTasks = tasks.map((t) =>
            t.id === task.id ? { ...t, start: task.start, end: task.end } : t
        );
        setTasks(updatedTasks);

        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
        };

        dispatch(editTask({
            id: task.id,
            data: {
                start_date: task.start ? formatDate(task.start) : "empty",
                start_date_is_visible: task.start ? 1 : 0,
                end_date: task.end ? formatDate(task.end) : "empty",
                end_date_is_visible: task.end ? 1 : 0,
                'updated_by': loggedInUser ? loggedInUser.loggedUserId : loggedUserId,
            },
        })).then((response) => {
            if (response.payload && response.payload.status === 200) {
                notifications.show({
                    color: 'green',
                    title: 'Success',
                    message: translate('Date updated successfully.'),
                    icon: <IconCheck />,
                    autoClose: 3000,
                });
            }
        });
    };



    return (
        <Fragment>
            <Box className="lazytask-gantt h-full flex flex-col">
                <Box className="flex-1 overflow-hidden flex flex-col relative" style={{ borderRadius: '8px', border: '1px solid #e9ecef', backgroundColor: 'white' }}>
                    <Box className="flex-1 overflow-hidden relative">
                        {tasks && tasks.length > 0 ? (
                            <Gantt
                                style={{ overflow: 'hidden' }}
                                listCellWidth={ganttListIsVisible ? "645px" : ""}
                                viewMode={ganttViewMode || ViewMode.Day}
                                viewDate={viewDate}
                                columnWidth={columnWidth}
                                rowHeight={40}
                                barFill={50}
                                tasks={tasks}
                                onExpanderClick={handleExpanderClick}
                                TaskListTable={(props) => (
                                    <TaskListTable
                                        {...props}
                                        setTasks={setTasks}
                                        onClick={onDblClick}
                                    />
                                )}
                                TaskListHeader={TaskListHeader}
                                TooltipContent={({ task }) => <CustomTooltip task={task} />}
                                ganttHeight={dynamicHeight}
                                onDoubleClick={onDblClick}
                                onClick={onDblClick}
                                onDateChange={handleDateChange}
                                barCornerRadius={4}
                                barProgressColor="#3b82f6"
                                barProgressSelectedColor="#2563eb"
                                barBackgroundColor="#e5e7eb"
                                barBackgroundSelectedColor="#d1d5db"
                            />
                        ) : (
                            <Card
                                withBorder
                                radius="md"
                                padding="md"
                                h={"100%"}
                                className="flex flex-col justify-center items-center bg-[#F9FAFB] shadow-sm"
                            >
                                <Box className="text-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-25">
                                    <ThemeIcon color='orange' variant="light" radius="md" size="xl">
                                        <IconNotesOff size={32} stroke={1.25} />
                                    </ThemeIcon>

                                    <Text ta="center" fz="lg" fw={700} c="#4A5568" mb="sm">
                                        {translate("No tasks available to display the Gantt chart.")}
                                    </Text>
                                    <Text ta="center" fz="sm" c="#718096" mb="lg">
                                        {translate("You can add tasks on gantt chart from list tab.")}
                                    </Text>

                                    <Button
                                        size="md"
                                        variant="light"
                                        color="#39758D"
                                        onClick={() => fetchTasks(true)}
                                        radius="md"
                                    >
                                        {translate("Refresh Tasks")}
                                    </Button>
                                </Box>
                            </Card>
                        )}
                    </Box>
                </Box>
            </Box>
        </Fragment>
    );
};

export default TaskGanttChart;
